import { toNullableText } from "../careersFormat";
import { JOB_LIMITS } from "../consts";
import type { CareersJob, CareersJobInput, CareersPostingChannel } from "../types";
import {
	JOB_DETAILS_PROBLEM_KEY,
	type JobDetailsForm,
	type JobDetailsProblem,
	jobDetailsFormFromJob,
	jobDetailsInput,
	jobDetailsProblems,
	newJobDetailsForm,
} from "./jobDetailsForm";

/**
 * The Post / Edit job form as editable strings, plus the conversions to and from the API.
 *
 * Every step and question row carries a client `key` (a stable React / reorder identity) and its server
 * `id` (null for a row added in this dialog). The id is what makes a PUT keep a step's candidates when it
 * is renamed or moved — so it must survive every edit and reorder untouched.
 */
export type JobStepRow = { key: string; id: string | null; name: string; applicantCount: number; isHiredStep: boolean };
export type JobQuestionRow = { key: string; id: string | null; question: string; desiredAnswer: string };
/** A criterion interviewers score (1-5). Its id keeps the scores already given under it on a rename. */
export type JobCriterionRow = { key: string; id: string | null; name: string };

export type JobForm = {
	title: string;
	team: string;
	openings: string;
	description: string;
	postingChannels: CareersPostingChannel[];
	/** The "Hiring now" label on the career page. Carried through every edit, like `details`, so a full-replace PUT keeps it. */
	hiringNow: boolean;
	steps: JobStepRow[];
	questions: JobQuestionRow[];
	criteria: JobCriterionRow[];
	/** What job boards ask about the job. Carried through every edit, so a full-replace PUT never clears them. */
	details: JobDetailsForm;
};

let rowSeed = 0;
/** A process-unique row key. Not the server id: new rows have none, and keys must never collide. */
export const newRowKey = (): string => {
	rowSeed += 1;
	return `row-${rowSeed}`;
};

export const newStepRow = (name: string, isHiredStep: boolean): JobStepRow => ({ key: newRowKey(), id: null, name, applicantCount: 0, isHiredStep });
export const newQuestionRow = (): JobQuestionRow => ({ key: newRowKey(), id: null, question: "", desiredAnswer: "" });
export const newCriterionRow = (name: string): JobCriterionRow => ({ key: newRowKey(), id: null, name });

/**
 * A new job: the default hiring steps and evaluation criteria (names already translated by the caller), no
 * questions, no details, posted nowhere yet and not marked "Hiring now".
 */
export const newJobForm = (defaultSteps: { name: string; isHiredStep: boolean }[], defaultCriteria: string[]): JobForm => ({
	title: "",
	team: "",
	openings: "1",
	description: "",
	postingChannels: [],
	hiringNow: false,
	steps: defaultSteps.map((step) => newStepRow(step.name, step.isHiredStep)),
	questions: [],
	criteria: defaultCriteria.map(newCriterionRow),
	details: newJobDetailsForm(),
});

export const jobFormFromJob = (job: CareersJob): JobForm => ({
	title: job.title,
	team: job.team === null ? "" : job.team,
	openings: String(job.openings),
	description: job.description,
	postingChannels: job.posting_channels,
	hiringNow: job.hiring_now,
	steps: job.steps.map((step) => ({
		key: newRowKey(),
		id: step.id,
		name: step.name,
		applicantCount: step.applicant_count,
		isHiredStep: step.is_hired_step,
	})),
	questions: job.questions.map((question) => ({
		key: newRowKey(),
		id: question.id,
		question: question.question,
		desiredAnswer: question.desired_answer === null ? "" : question.desired_answer,
	})),
	criteria: job.criteria.map((criterion) => ({ key: newRowKey(), id: criterion.id, name: criterion.name })),
	details: jobDetailsFormFromJob(job),
});

/**
 * The form of a SAVED job with its steps, questions and criteria in position order — the order the board,
 * applicants and the feedback form see, and the order a full-replace PUT must send so nothing moves.
 */
const jobFormFromSavedJob = (job: CareersJob): JobForm =>
	jobFormFromJob({
		...job,
		steps: [...job.steps].sort((a, b) => a.position - b.position),
		questions: [...job.questions].sort((a, b) => a.position - b.position),
		criteria: [...job.criteria].sort((a, b) => a.position - b.position),
	});

/**
 * A NEW job's form, filled from `job` (live or closed): its team, openings, description, posting channels, its "Hiring now" mark, every
 * step in order (the hired step still marked), every screening question with its wanted answer, every
 * criterion and its details. `copyTitle` is the translated "<title> (copy)". Every row loses its server id and its candidates — so
 * the save creates fresh rows, nothing is locked by the source's candidates, and the source job is never touched.
 */
export const jobFormCopyOf = (job: CareersJob, copyTitle: string): JobForm => {
	const source = jobFormFromSavedJob(job);
	return {
		...source,
		title: copyTitle,
		steps: source.steps.map((step) => ({ ...step, id: null, applicantCount: 0 })),
		questions: source.questions.map((question) => ({ ...question, id: null })),
		criteria: source.criteria.map((criterion) => ({ ...criterion, id: null })),
	};
};

export type JobFormProblem =
	| JobDetailsProblem
	| "title_required"
	| "openings_invalid"
	| "steps_too_few"
	| "step_blank"
	| "steps_duplicate"
	| "steps_multiple_hired"
	| "hired_step_first"
	| "question_blank"
	| "criterion_blank"
	| "criteria_duplicate";

/** What each problem says — the job dialog's Save tooltip and the Jobs table's refused inline edit both read it. */
export const JOB_FORM_PROBLEM_KEY: Record<JobFormProblem, string> = {
	title_required: "careers.job_dialog.problem.title_required",
	openings_invalid: "careers.job_dialog.problem.openings_invalid",
	steps_too_few: "careers.steps.too_few",
	step_blank: "careers.job_dialog.problem.step_blank",
	steps_duplicate: "careers.steps.duplicate_name",
	steps_multiple_hired: "careers.job.multiple_hired_steps",
	hired_step_first: "careers.job_dialog.problem.hired_step_first",
	question_blank: "careers.job_dialog.problem.question_blank",
	criterion_blank: "careers.job_dialog.problem.criterion_blank",
	criteria_duplicate: "careers.criteria.duplicate_name",
	...JOB_DETAILS_PROBLEM_KEY,
};

/**
 * Press the hired-step toggle on one row: it becomes the ONLY hired step, or — pressed again — the job has
 * none. So the form can never hold two, which the backend would refuse.
 */
export const toggleHiredStep = (steps: JobStepRow[], key: string): JobStepRow[] => {
	const turningOff = steps.some((step) => step.key === key && step.isHiredStep);
	return steps.map((step) => ({ ...step, isHiredStep: !turningOff && step.key === key }));
};

/**
 * Only a step after the first can be the hired step: new applicants land in the first step, and landing
 * there must not mark them hired.
 */
export const canMarkHiredStep = (index: number): boolean => index > 0;

/**
 * The Hired mark can't leave a saved hired step that holds candidates: they are all hired, and taking the
 * mark off (or moving it elsewhere, which takes it off) would strand them — the backend refuses it with 409
 * `careers.step.has_applicants`. Returns how many hold it in place, or 0 when the mark is free to move.
 */
export const hiredMarkHolders = (steps: JobStepRow[]): number => {
	const hired = steps.find((step) => step.isHiredStep && step.id !== null);
	return hired === undefined ? 0 : hired.applicantCount;
};

const parseOpenings = (text: string): number | null => {
	if (!/^\d+$/.test(text.trim())) return null;
	const value = Number(text.trim());
	return value >= JOB_LIMITS.MIN_OPENINGS && value <= JOB_LIMITS.MAX_OPENINGS ? value : null;
};

/**
 * What stops this form from saving — the backend's rules, checked up front so Save is disabled with a
 * reason rather than failing with one. Empty list = savable.
 */
export const jobFormProblems = (form: JobForm): JobFormProblem[] => {
	const problems: JobFormProblem[] = [];
	if (form.title.trim() === "") problems.push("title_required");
	if (parseOpenings(form.openings) === null) problems.push("openings_invalid");
	const names = form.steps.map((step) => step.name.trim());
	if (names.length < JOB_LIMITS.MIN_STEPS) problems.push("steps_too_few");
	if (names.some((name) => name === "")) problems.push("step_blank");
	const folded = names.filter(Boolean).map((name) => name.toLocaleLowerCase());
	if (new Set(folded).size !== folded.length) problems.push("steps_duplicate");
	if (form.steps.filter((step) => step.isHiredStep).length > 1) problems.push("steps_multiple_hired");
	if (form.steps.length > 0 && form.steps[0].isHiredStep) problems.push("hired_step_first");
	if (form.questions.some((question) => question.question.trim() === "")) problems.push("question_blank");
	const criteria = form.criteria.map((criterion) => criterion.name.trim());
	if (criteria.some((name) => name === "")) problems.push("criterion_blank");
	// toLowerCase, as the backend's lower(name) unique index compares them.
	const foldedCriteria = criteria.filter(Boolean).map((name) => name.toLowerCase());
	if (new Set(foldedCriteria).size !== foldedCriteria.length) problems.push("criteria_duplicate");
	return [...problems, ...jobDetailsProblems(form.details)];
};

/**
 * `job` with one more step, `name`, at `index` of its steps — everything else as saved, so the PUT keeps every
 * step's candidates. `isHiredStep` makes it the job's hired step: a drop on "All jobs"' hired lane hires the
 * candidate in every other job, so it has to here too. Throws if that job would not save (a name it already has,
 * too many steps, a second hired step).
 */
export const jobInputWithStep = (job: CareersJob, name: string, index: number, isHiredStep: boolean): CareersJobInput => {
	const form = jobFormFromSavedJob(job);
	if (form.steps.length >= JOB_LIMITS.MAX_STEPS) throw new Error(`Job ${job.id} already has ${JOB_LIMITS.MAX_STEPS} steps`);
	const steps = [...form.steps.slice(0, index), newStepRow(name, isHiredStep), ...form.steps.slice(index)];
	const next = { ...form, steps };
	const problems = jobFormProblems(next);
	if (problems.length > 0) throw new Error(`Adding step "${name}" to job ${job.id}: ${problems.join(", ")}`);
	return jobInputFromForm(next);
};

/** One screening question as the Jobs table's panel edits it: an existing one (`id`) or a new one (`id: null`). */
export type JobQuestionDraft = { id: string | null; question: string; desiredAnswer: string };

/**
 * `job` with one question changed (`draft.id` set) or appended (`draft.id` null) — every step and every other
 * question as saved, ids kept, so the full-replace PUT keeps the steps' candidates and the other questions'
 * answers. A blank `desiredAnswer` means no wanted answer, as in the job dialog. Throws if that job would not
 * save (a blank question, no such question, too many questions).
 */
export const jobInputWithQuestion = (job: CareersJob, draft: JobQuestionDraft): CareersJobInput => {
	const form = jobFormFromSavedJob(job);
	const edited = { question: draft.question, desiredAnswer: draft.desiredAnswer };
	let questions: JobQuestionRow[];
	if (draft.id === null) {
		if (form.questions.length >= JOB_LIMITS.MAX_QUESTIONS) throw new Error(`Job ${job.id} already has ${JOB_LIMITS.MAX_QUESTIONS} questions`);
		questions = [...form.questions, { ...newQuestionRow(), ...edited }];
	} else {
		if (!form.questions.some((row) => row.id === draft.id)) throw new Error(`Job ${job.id} has no question ${draft.id}`);
		questions = form.questions.map((row) => (row.id === draft.id ? { ...row, ...edited } : row));
	}
	const next = { ...form, questions };
	const problems = jobFormProblems(next);
	if (problems.length > 0) throw new Error(`Saving question on job ${job.id}: ${problems.join(", ")}`);
	return jobInputFromForm(next);
};

/**
 * The saved job's full-replace body without one question: every step and every other question as saved, ids
 * kept, so the server deletes only that one. Answers candidates already gave to it stay on their profiles — each
 * answer keeps its own copy of the question. Throws if the job has no such question.
 */
export const jobInputWithoutQuestion = (job: CareersJob, questionId: string): CareersJobInput => {
	const form = jobFormFromSavedJob(job);
	if (!form.questions.some((row) => row.id === questionId)) throw new Error(`Job ${job.id} has no question ${questionId}`);
	return jobInputFromForm({ ...form, questions: form.questions.filter((row) => row.id !== questionId) });
};

/**
 * The job-level fields the Jobs table edits in place. A key that is present is the new value — `team: null`
 * clears the team — and an absent key keeps the saved one. `openings` is whatever number the cell's editor
 * gave (it may be fractional, zero or NaN); the same rules as the dialog decide whether it saves.
 */
export type JobFieldsPatch = {
	title?: string;
	team?: string | null;
	openings?: number;
	posting_channels?: CareersPostingChannel[];
};

/** The saved job's form with `patch` applied — every step and question in position order, ids kept. */
const jobFormWithFields = (job: CareersJob, patch: JobFieldsPatch): JobForm => {
	const form = jobFormFromSavedJob(job);
	return {
		...form,
		title: patch.title === undefined ? form.title : patch.title,
		team: patch.team === undefined ? form.team : patch.team === null ? "" : patch.team,
		openings: patch.openings === undefined ? form.openings : String(patch.openings),
		postingChannels: patch.posting_channels === undefined ? form.postingChannels : patch.posting_channels,
	};
};

/** Why `job` with `patch` would not save (the dialog's own rules) — empty when it would. */
export const jobFieldsProblems = (job: CareersJob, patch: JobFieldsPatch): JobFormProblem[] => jobFormProblems(jobFormWithFields(job, patch));

/**
 * `job` with its title, team, openings and/or posting channels changed — every step and every question as saved,
 * ids kept, so the full-replace PUT keeps each step's candidates and each question's answers. Throws if that job
 * would not save (check `jobFieldsProblems` first to say why instead).
 */
export const jobInputWithFields = (job: CareersJob, patch: JobFieldsPatch): CareersJobInput => {
	const next = jobFormWithFields(job, patch);
	const problems = jobFormProblems(next);
	if (problems.length > 0) throw new Error(`Saving fields ${Object.keys(patch).join(",")} on job ${job.id}: ${problems.join(", ")}`);
	return jobInputFromForm(next);
};

/**
 * The saved job as the job dialog's form, every list in position order — what a Jobs-table cell editing one part
 * of the job (its criteria) opens on, and saves back whole.
 */
export const savedJobForm = (job: CareersJob): JobForm => jobFormFromSavedJob(job);

/** Whether `form` would save anything the saved job doesn't already say. */
export const jobFormChanged = (job: CareersJob, form: JobForm): boolean =>
	JSON.stringify(jobInputFromForm(form)) !== JSON.stringify(jobInputFromForm(jobFormFromSavedJob(job)));

/** The saved job's hiring steps as editable rows, in position order — what the Jobs table's Steps panel opens on. */
export const jobStepRows = (job: CareersJob): JobStepRow[] => jobFormFromSavedJob(job).steps;

/** Why the saved job with these steps would not save (the job dialog's own step rules); empty when it would. */
export const jobStepsProblems = (job: CareersJob, steps: JobStepRow[]): JobFormProblem[] => jobFormProblems({ ...jobFormFromSavedJob(job), steps });

/** Whether `steps` say anything the saved job doesn't: a name (spacing aside), the order, the hired mark, a step added or removed. */
export const jobStepsChanged = (job: CareersJob, steps: JobStepRow[]): boolean => {
	const saved = jobStepRows(job);
	return (
		saved.length !== steps.length ||
		steps.some((step, index) => step.id !== saved[index].id || step.name.trim() !== saved[index].name || step.isHiredStep !== saved[index].isHiredStep)
	);
};

/**
 * The saved job's full-replace body with `steps` as its hiring steps — every other field and every question as
 * saved, ids kept, so a kept step keeps its candidates. Throws if that job would not save (check
 * `jobStepsProblems` first to say why instead).
 */
export const jobInputWithSteps = (job: CareersJob, steps: JobStepRow[]): CareersJobInput => {
	const next = { ...jobFormFromSavedJob(job), steps };
	const problems = jobFormProblems(next);
	if (problems.length > 0) throw new Error(`Saving steps on job ${job.id}: ${problems.join(", ")}`);
	return jobInputFromForm(next);
};

/**
 * The teams this department's jobs already use — distinct, non-blank, A→Z — offered wherever a job's team is
 * chosen (the job dialog, the Jobs table's Team cell) so the same team is picked rather than retyped. Spellings
 * that differ only in case or spacing are one team, listed as its first job spells it.
 */
export const jobTeamOptions = (jobs: readonly Pick<CareersJob, "team">[]): string[] => {
	const byKey = new Map<string, string>();
	for (const { team } of jobs) {
		const name = team === null ? "" : team.trim();
		const key = name.toLocaleLowerCase();
		if (name !== "" && !byKey.has(key)) byKey.set(key, name);
	}
	return [...byKey.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

/** The request body. Only call once `jobFormProblems` is empty — it throws on unparseable openings or details. */
export const jobInputFromForm = (form: JobForm): CareersJobInput => {
	const openings = parseOpenings(form.openings);
	if (openings === null) throw new Error("jobInputFromForm called with invalid openings");
	return {
		title: form.title.trim(),
		team: toNullableText(form.team),
		openings,
		description: form.description.trim(),
		posting_channels: form.postingChannels,
		hiring_now: form.hiringNow,
		steps: form.steps.map((step) => ({ id: step.id, name: step.name.trim(), is_hired_step: step.isHiredStep })),
		questions: form.questions.map((question) => ({
			id: question.id,
			question: question.question.trim(),
			desired_answer: toNullableText(question.desiredAnswer),
		})),
		criteria: form.criteria.map((criterion) => ({ id: criterion.id, name: criterion.name.trim() })),
		...jobDetailsInput(form.details),
		// Only the job dialog's Indeed card posts the job too, and it sets this on the body it saves.
		indeed_posting: null,
	};
};
